import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth0 } from '../../../auth/useAuth0';
import {
    ClinicalRecordMeta, clinicalRecords, clinicalRecordContent,
    saveClinicalRecord, signClinicalRecord,
} from '../../../service/clinical/clinical-service';
import { getServicesByBusinessId } from '../../../service/service/service-service';
import { getTeamMembersByBusinessId } from '../../../service/teammember/team-member-service';

/** SOAP-style structured sections stored as JSON in the encrypted content. */
const SECTIONS = ['queixa', 'historico', 'avaliacao', 'plano'] as const;
type SectionKey = typeof SECTIONS[number];
type Sections = Record<SectionKey, string>;
const emptySections = (): Sections => ({ queixa: '', historico: '', avaliacao: '', plano: '' });

interface Props {
    businessId: number;
    clientId: number;
}

/**
 * Prontuário timeline inside the client drawer. Metadata for everyone with the
 * CLINICAL_RECORDS permission; CONTENT opens only for the professional of the
 * attendance — the backend care gate decides, the UI just reflects it.
 */
const ClinicalRecordsTab: React.FC<Props> = ({ businessId, clientId }) => {
    const { t } = useTranslation();
    const { getAccessTokenSilently } = useAuth0();
    const [records, setRecords] = useState<ClinicalRecordMeta[]>([]);
    const [openId, setOpenId] = useState<string | null>(null); // null = list; 'new' = create
    const [sections, setSections] = useState<Sections>(emptySections());
    const [openMeta, setOpenMeta] = useState<ClinicalRecordMeta | null>(null);
    const [lockedMsg, setLockedMsg] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [memberNames, setMemberNames] = useState<Record<string, string>>({});
    const [serviceNames, setServiceNames] = useState<Record<string, string>>({});

    // Name lookups are cosmetic — the timeline still works showing ids if they fail.
    useEffect(() => {
        (async () => {
            try {
                const token = await getAccessTokenSilently();
                const [members, services] = await Promise.all([
                    getTeamMembersByBusinessId(businessId, token).catch(() => []),
                    getServicesByBusinessId(businessId, token).catch(() => []),
                ]);
                const mn: Record<string, string> = {};
                (members as any[]).forEach((m) => { mn[String(m.id)] = `${m.firstName ?? ''} ${m.lastName ?? ''}`.trim(); });
                const sn: Record<string, string> = {};
                (services as any[]).forEach((sv) => { sn[String(sv.id)] = sv.name ?? ''; });
                setMemberNames(mn); setServiceNames(sn);
            } catch { /* cosmetic */ }
        })();
    }, [businessId, getAccessTokenSilently]);

    const load = useCallback(async () => {
        try {
            const token = await getAccessTokenSilently();
            setRecords(await clinicalRecords(businessId, clientId, token));
            setError(null);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'error');
        }
    }, [businessId, clientId, getAccessTokenSilently]);

    useEffect(() => { void load(); }, [load]);

    const openRecord = async (r: ClinicalRecordMeta) => {
        setBusy(true); setLockedMsg(null);
        try {
            const token = await getAccessTokenSilently();
            const res = await clinicalRecordContent(businessId, r.id, token);
            let parsed: Sections = emptySections();
            try { parsed = { ...parsed, ...JSON.parse(res.content) }; } catch { parsed.historico = res.content; }
            setSections(parsed); setOpenMeta(res.record); setOpenId(r.id);
        } catch (e) {
            // Care gate: existence is visible, content is not.
            setLockedMsg(e instanceof Error ? e.message : t('clinical.locked'));
            setOpenMeta(r); setOpenId(r.id);
        } finally { setBusy(false); }
    };

    const save = async (sign: boolean) => {
        setBusy(true);
        try {
            const token = await getAccessTokenSilently();
            const saved = await saveClinicalRecord(businessId, {
                id: openId === 'new' ? null : openId,
                clientId, content: JSON.stringify(sections),
            }, token);
            if (sign) await signClinicalRecord(businessId, saved.id, token);
            setOpenId(null); setOpenMeta(null); setLockedMsg(null);
            await load();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'error');
        } finally { setBusy(false); }
    };

    if (openId !== null) {
        const signed = openMeta?.status === 'SIGNED';
        return (
            <div className="gs-client-history" data-cy="clinical-editor">
                <button type="button" className="btn btn-link btn-sm px-0" onClick={() => { setOpenId(null); setLockedMsg(null); }}>
                    ← {t('clinical.back')}
                </button>
                {lockedMsg ? (
                    <div className="alert alert-secondary py-2 px-3" data-cy="clinical-locked">🔒 {lockedMsg}</div>
                ) : (
                    <>
                        {SECTIONS.map((k) => (
                            <div className="form-group" key={k}>
                                <label>{t(`clinical.section.${k}`)}</label>
                                <textarea className="form-control" rows={2} value={sections[k]} disabled={signed || busy}
                                    onChange={(e) => setSections((p) => ({ ...p, [k]: e.target.value }))}
                                    data-cy={`clinical-section-${k}`} />
                            </div>
                        ))}
                        {signed ? (
                            <div className="alert alert-success py-1 px-2">✍️ {t('clinical.signedNote')}</div>
                        ) : (
                            <div className="d-flex gap-2">
                                <button type="button" className="btn btn-outline-primary btn-sm" disabled={busy}
                                        onClick={() => void save(false)} data-cy="clinical-save-draft">
                                    {t('clinical.saveDraft')}
                                </button>
                                <button type="button" className="btn btn-primary btn-sm" disabled={busy}
                                        onClick={() => void save(true)} data-cy="clinical-sign">
                                    ✍️ {t('clinical.sign')}
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        );
    }

    return (
        <div className="gs-client-history" data-cy="clinical-tab">
            {error && <div className="alert alert-danger py-1 px-2">{error}</div>}
            {records.length === 0 && <div className="text-muted">{t('clinical.empty')}</div>}
            {records.map((r) => (
                <div key={r.id} className="list-item" style={{ cursor: 'pointer' }} onClick={() => void openRecord(r)}
                     data-cy={`clinical-row-${r.id}`}>
                    <div>
                        <span className="name">
                            {(r.createdOn ?? '').substring(0, 10)} — {serviceNames[r.serviceId ?? ''] ?? t('clinical.record')}
                        </span>
                        <span className={`badge rounded-pill ms-2 ${r.status === 'SIGNED' ? 'text-bg-success' : 'text-bg-warning'}`}>
                            {r.status === 'SIGNED' ? t('clinical.signed') : t('clinical.draft')}
                        </span>
                        <div className="meta">
                            {memberNames[r.teamMemberId] ?? `#${r.teamMemberId}`}
                            {r.appointmentId ? ` · ${t('clinical.fromAppointment', { id: r.appointmentId })}` : ''}
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
};

export default ClinicalRecordsTab;
